import * as React from 'react';

import type { GetGlobalHistoryResponse } from '@blockworks/platform/api/research';
import type { PostType } from '@blockworks/platform/api/strapi';
import { CompactNumber } from '@blockworks/ui';
import { cn } from '@blockworks/ui/utils';

import MarketDataCard from '@/components/display/cards/market-data-card';
import Newsletter from '@/components/pages/frontpage/components/feature/right/newsletter';
import ResearchTeaser from '@/components/pages/frontpage/components/feature/right/research-teaser';
import subscribeToNewsletter from '@/utils/functions/subscribe-to-newsletter';

type RightColumnWithMarketsDataProps = {
    className?: string;
    currentGlobalMarketData?: GetGlobalHistoryResponse['data'][number];
    currentGlobalMarketChange?: number;
    currentGlobalMarketVolumeChange?: number;
    globalMarketHistory?: GetGlobalHistoryResponse['data'];
    recentResearch?: PostType;
    showNewsletter?: boolean;
};

const RightColumnWithMarketsData = (props: RightColumnWithMarketsDataProps) => {
    const {
        className,
        currentGlobalMarketData,
        currentGlobalMarketChange,
        currentGlobalMarketVolumeChange,
        globalMarketHistory,
        recentResearch,
        showNewsletter = true,
    } = props;

    return (
        <div className={cn('flex flex-wrap xl:flex-col', className)}>
            <div className="">
                <div className="md:flex xl:flex-col">
                    <div className="order-1 xl:order-0 md:w-6/12 xl:w-full">
                        <div className="flex xl:flex-col justify-start items-start">
                            <div className="flex justify-start items-center px-10 py-5 w-full border-b">
                                <div className="gap-2.5">
                                    <p className="font-semibold text-left uppercase text-gray-900 whitespace-nowrap">
                                        market data
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="grid sm:grid-cols-2 md:grid-cols-1 lg:divide-y divide-gray-200 mt-0 m-10 gap-10">
                            <MarketDataCard
                                className="pt-10"
                                title="Total Market Cap"
                                movePercentage={currentGlobalMarketChange}
                                sparklineData={globalMarketHistory?.map(g => g.totalUsdMarketCap)}
                            >
                                <CompactNumber
                                    number={currentGlobalMarketData?.totalUsdMarketCap ?? 0}
                                    fractionDigits={2}
                                />
                            </MarketDataCard>
                            <MarketDataCard
                                className="pt-10"
                                title="24h Volume"
                                movePercentage={currentGlobalMarketVolumeChange}
                                sparklineData={globalMarketHistory?.map(g => g.totalUsdVolume24H)}
                            >
                                <CompactNumber
                                    number={currentGlobalMarketData?.totalUsdVolume24H ?? 0}
                                    fractionDigits={2}
                                />
                            </MarketDataCard>
                            <MarketDataCard
                                className="pt-10"
                                sparklineData={globalMarketHistory?.map(g => g.btcDominanceChange24H)}
                                title={
                                    <div className="flex justify-start items-center relative gap-1">
                                        <svg
                                            width="10"
                                            height="10"
                                            viewBox="0 0 10 10"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="w-2.5 h-2.5 relative"
                                            preserveAspectRatio="xMidYMid meet"
                                        >
                                            <g clipPath="url(#clip0_1428_46617)">
                                                <path
                                                    d="M0.868586 2.50277C1.02279 2.28745 1.16609 2.06475 1.33983 1.86305C1.65583 1.49619 2.01367 1.18179 2.42343 0.921551C2.79101 0.688111 3.17865 0.504111 3.59345 0.375451C4.23933 0.175091 4.89865 0.115511 5.57021 0.192151C6.0611 0.248171 6.53404 0.373491 6.98448 0.576451C7.25714 0.699291 7.5136 0.851071 7.76498 1.01499C8.27154 1.34529 8.67734 1.77527 9.01658 2.26653C9.3789 2.79125 9.61436 3.37495 9.7435 4.00103C9.8342 4.44069 9.87248 4.88446 9.83356 5.33112C9.78558 5.88184 9.66328 6.41284 9.4348 6.9232C9.24156 7.35486 9.00302 7.75122 8.69758 8.10922C8.39878 8.45944 8.06182 8.76584 7.6746 9.01844C7.41092 9.19044 7.13608 9.34396 6.84592 9.45794C6.36044 9.64868 5.85698 9.77766 5.33017 9.80024C4.95927 9.81614 4.59047 9.8099 4.22459 9.74498C3.91033 9.68922 3.60361 9.60782 3.30333 9.49506C3.01201 9.38566 2.72947 9.25606 2.46915 9.0922C2.09383 8.85596 1.75549 8.56926 1.45441 8.23894C1.11895 7.87088 0.846946 7.46278 0.638826 7.01206C0.344825 6.37536 0.200245 5.70404 0.191645 5.00356C0.188405 4.7401 0.218625 4.47867 0.253745 4.21789C0.256265 4.19923 0.253705 4.17991 0.253505 4.16087C0.284225 4.14183 0.281505 4.10851 0.287045 4.07945C0.365165 3.66979 0.496925 3.27827 0.675406 2.90133C0.738646 2.76785 0.825906 2.64605 0.868586 2.50277ZM6.50314 5.09018C6.63388 5.0207 6.75812 4.98088 6.8563 4.89318C7.23466 4.55519 7.3355 3.75971 6.72136 3.37651C6.53004 3.25713 6.32596 3.16865 6.10596 3.07837C6.14234 2.78375 6.26014 2.50805 6.2984 2.20631C6.11688 2.16133 5.94684 2.10163 5.7699 2.08245C5.69756 2.36599 5.6282 2.63783 5.55642 2.91919C5.42082 2.87767 5.28119 2.88161 5.15357 2.80973C5.19961 2.52193 5.30903 2.25229 5.33893 1.96345C5.15249 1.93417 4.99461 1.86157 4.81363 1.85375C4.74237 2.13793 4.67341 2.41291 4.60191 2.69805C4.24303 2.64305 3.91353 2.51963 3.55151 2.45327C3.47197 2.63147 3.43675 2.81949 3.40483 3.01953C3.52275 3.04737 3.62527 3.07199 3.72799 3.09575C3.98729 3.15571 4.05833 3.29747 3.99677 3.52423C3.95297 3.68553 3.91345 3.84801 3.87313 4.01025C3.72823 4.59345 3.58449 5.17692 3.43873 5.7599C3.40589 5.89124 3.32405 5.93402 3.19077 5.91452C3.05951 5.89532 2.93563 5.83638 2.79097 5.84066C2.72825 6.0413 2.60341 6.21808 2.55921 6.43958C2.90393 6.5274 3.23743 6.61236 3.58609 6.70118C3.55985 7.00534 3.43381 7.28368 3.39657 7.59C3.57135 7.6319 3.73587 7.67132 3.90745 7.71246C4.02051 7.43158 4.04659 7.1364 4.15197 6.84586C4.27899 6.91116 4.41991 6.8922 4.54421 6.9744C4.47647 7.25446 4.40897 7.53344 4.34065 7.81594C4.52037 7.88026 4.69571 7.9065 4.87581 7.95134C4.94871 7.65926 5.01919 7.37678 5.08737 7.10362C5.12983 7.0818 5.16089 7.08586 5.19023 7.09214C5.4725 7.15256 5.75804 7.16666 6.04376 7.13494C6.29112 7.10748 6.51062 7.01318 6.68672 6.8275C6.88254 6.62104 6.98092 6.36846 7.02828 6.0968C7.06688 5.8755 7.057 5.65126 6.92708 5.45538C6.82526 5.3019 6.6854 5.18478 6.50314 5.09018ZM4.46255 6.24332C4.81077 6.32982 5.15861 6.42232 5.52296 6.3558C5.71726 6.32032 5.8637 6.2212 5.92266 6.02574C5.97826 5.84138 5.94594 5.67388 5.8092 5.52816C5.667 5.37664 5.48412 5.29788 5.29556 5.23202C5.07027 5.15334 4.84189 5.08186 4.59575 5.06242C4.50143 5.44578 4.40989 5.81788 4.31631 6.19818C4.37593 6.21676 4.41865 6.23242 4.46255 6.24332ZM5.69256 4.63683C5.88512 4.60947 6.0448 4.48207 6.09172 4.30897C6.1491 4.09731 6.08498 3.91167 5.91368 3.78541C5.71932 3.64215 5.49552 3.56927 5.26427 3.51867C5.17813 3.49981 5.09339 3.44959 4.98915 3.48349C4.90629 3.82023 4.82309 4.15843 4.73717 4.50767C5.06631 4.59078 5.36939 4.68273 5.69256 4.63683Z"
                                                    fill="#F39321"
                                                />
                                            </g>
                                            <defs>
                                                <clipPath id="clip0_1428_46617">
                                                    <rect width="10" height="10" fill="white" />
                                                </clipPath>
                                            </defs>
                                        </svg>
                                        <p className="text-xs text-left uppercase text-gray-900">btc dominance</p>
                                    </div>
                                }
                                movePercentage={currentGlobalMarketData?.btcDominanceChange24H}
                            >
                                <span>
                                    <CompactNumber
                                        number={currentGlobalMarketData?.btcDominance ?? 0}
                                        fractionDigits={2}
                                    />
                                    %
                                </span>
                            </MarketDataCard>
                            <MarketDataCard
                                className="pt-10"
                                sparklineData={globalMarketHistory?.map(g => g.ethDominanceChange24H)}
                                title={
                                    <div className="flex justify-start items-center relative gap-1">
                                        <svg
                                            width="10"
                                            height="10"
                                            viewBox="0 0 10 10"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="w-2.5 h-2.5 relative"
                                            preserveAspectRatio="xMidYMid meet"
                                        >
                                            <path
                                                d="M4.99976 0.131104L7.95972 5.02227L5.01072 3.76187L4.99976 0.131104Z"
                                                fill="#2F3030"
                                            />
                                            <path
                                                d="M2.04785 5.02227L4.99969 0.131104L5.01065 3.76187L2.04785 5.02227Z"
                                                fill="#828384"
                                            />
                                            <path
                                                d="M4.99969 6.82558L2.04785 5.02212L5.01065 3.76172L4.99969 6.82558Z"
                                                fill="#343535"
                                            />
                                            <path
                                                d="M7.95972 5.02212L5.01072 3.76172L4.99976 6.82558L7.95972 5.02212Z"
                                                fill="#131313"
                                            />
                                            <path
                                                d="M4.99976 7.44654L7.95972 5.69189L4.99976 9.86256V7.44654Z"
                                                fill="#2F3030"
                                            />
                                            <path
                                                d="M4.99969 7.44654L2.04785 5.69189L4.99969 9.86256V7.44654Z"
                                                fill="#828384"
                                            />
                                        </svg>
                                        <p className="text-xs text-left uppercase text-gray-900">eth dominance</p>
                                    </div>
                                }
                                movePercentage={currentGlobalMarketData?.ethDominanceChange24H}
                            >
                                <span>
                                    <CompactNumber
                                        number={currentGlobalMarketData?.ethDominance ?? 0}
                                        fractionDigits={2}
                                    />
                                    %
                                </span>
                            </MarketDataCard>
                        </div>
                    </div>
                    {recentResearch && (
                        <div className="flex flex-col justify-start w-full xl:order-1 xl:border-t">
                            <div className="flex justify-start items-center px-10 py-5 w-full border-b">
                                <div className="gap-2.5">
                                    <p className="font-semibold text-left uppercase text-gray-900">recent research</p>
                                </div>
                            </div>
                            <ResearchTeaser headerTitle="Research" article={recentResearch} className="p-10" />
                        </div>
                    )}
                </div>
            </div>
            {showNewsletter && (
                <div className="w-full border-t xl:border-b">
                    <Newsletter
                        title="Newsletter"
                        subTitle="The Breakdown"
                        inputPlaceholder="Email address"
                        description="Decoding crypto and the markets. Daily, with Byron Gilliam."
                        buttonText="Subscribe"
                        onButtonClick={subscribeToNewsletter}
                    />
                </div>
            )}
        </div>
    );
};

export default RightColumnWithMarketsData;
